import {
  Film, Boxes, Smile, BookOpen, Swords, Zap, Sparkles, Palette, Pencil, Shuffle,
  Check, ArrowLeft, ArrowRight, Wand2,
} from "lucide-react";
import { ComboField, MultiField } from "@/components/builder/Fields";
import { NAME_OPTIONS, BACKSTORY, DETAIL_FIELDS, STYLE_CARDS, PET_CREATURE_TYPES, PET_ROLES } from "@/lib/builderOptions";

const ICONS = { Film, Boxes, Smile, BookOpen, Swords, Zap, Sparkles, Palette, Pencil, Shuffle };

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "center" });

function StyleCards({ value, onChange }) {
  return (
    <div id="field-style" className="rounded-3xl border border-[#4A235A]/10 bg-white/70 p-5 sm:p-6">
      <h2 className="font-display text-xl font-bold text-[#4A235A]">
        Choose your finished image style <span className="text-[#9B5DE5]">*</span>
      </h2>
      <p className="mt-1 text-sm font-medium text-[#5C4A61]">
        This controls how your transformed character will look. Your original drawing's most recognizable features will still be preserved.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {STYLE_CARDS.map((s) => {
          const Icon = ICONS[s.icon] || Sparkles;
          const active = value === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => onChange(s.id)}
              data-testid={`style-${s.id.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}
              aria-pressed={active}
              className={`group flex flex-col overflow-hidden rounded-2xl border-2 text-left transition-transform hover:-translate-y-0.5 ${
                active ? "border-[#F4D03F] ring-2 ring-[#F4D03F]/50" : "border-transparent"
              }`}
            >
              <span className={`relative grid h-20 place-items-center bg-gradient-to-br ${s.grad}`}>
                <Icon size={26} className="text-white/90" />
                {active && (
                  <span className="absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full bg-[#F4D03F] text-[#1A0F1C]">
                    <Check size={13} />
                  </span>
                )}
              </span>
              <span className="flex flex-1 flex-col gap-0.5 bg-white p-2.5">
                <span className="text-sm font-bold leading-tight text-[#1A0F1C]">{s.id}</span>
                <span className="text-[11px] leading-snug text-[#5C4A61]">{s.desc}</span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="mt-3 text-xs font-semibold text-[#4A235A]">
        Selected: <span className="text-[#9B5DE5]">{value || "none yet"}</span>
      </p>
    </div>
  );
}

function SummaryRow({ label, value, fieldId }) {
  const shown = Array.isArray(value) ? (value.length ? value.join(", ") : "—") : value || "—";
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[#4A235A]/8 py-2">
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-wide text-[#9A8AA0]">{label}</p>
        <p className="truncate text-sm font-semibold text-[#1A0F1C]">{shown}</p>
      </div>
      <button
        type="button"
        onClick={() => scrollTo(fieldId)}
        data-testid={`summary-edit-${fieldId}`}
        className="shrink-0 text-xs font-bold text-[#9B5DE5] hover:underline"
      >
        Edit
      </button>
    </div>
  );
}

export function CharacterBuilder({ value, setValue, stepLabel, onBack, onContinue, submitting, isPet = false }) {
  const set = (k, v) => setValue({ ...value, [k]: v });

  const detailFields = DETAIL_FIELDS.map((f) => {
    if (!isPet) return f;
    if (f.key === "type")
      return { ...f, label: "What fantasy form should your pet become?", options: PET_CREATURE_TYPES, placeholder: "Choose a fantasy form" };
    if (f.key === "role") return { ...f, options: PET_ROLES };
    return f;
  });

  const missing = [];
  if (!value.name) missing.push("character name");
  if (!value.type) missing.push(isPet ? "fantasy form" : "creature type");
  if (!value.visual_style) missing.push("image style");
  const canContinue = missing.length === 0 && !submitting;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm font-bold uppercase tracking-[0.15em] text-[#9B5DE5]" data-testid="builder-step-label">{stepLabel}</p>
        <h1 className="mt-1 font-display text-3xl font-black text-[#1A0F1C] sm:text-4xl">
          Build Your Legendary {isPet ? "Pet" : "Character"}
        </h1>
        <p className="mt-2 max-w-xl text-sm font-medium text-[#5C4A61]">
          {isPet
            ? "Turn your pet into a magical legend. Choose a fantasy form and details, or let AI surprise you. You'll review everything and the price before payment."
            : "Choose from our suggestions or create something completely original. You'll review your complete order and price before payment."}
        </p>
      </div>

      <StyleCards value={value.visual_style} onChange={(v) => set("visual_style", v)} />

      <div className="grid gap-5 sm:grid-cols-2">
        <ComboField
          label="Character name" testid="name" value={value.name} onChange={(v) => set("name", v)}
          options={NAME_OPTIONS} customLabel="Custom Name…" customMax={50} placeholder="Choose or name it"
        />
        <div className="hidden sm:block" />
        {detailFields.map((f) =>
          f.kind === "single" ? (
            <ComboField
              key={f.key} label={f.label} testid={f.key} value={value[f.key]} onChange={(v) => set(f.key, v)}
              options={f.options} customLabel={f.custom} placeholder={f.placeholder}
            />
          ) : (
            <MultiField
              key={f.key} label={f.label} testid={f.key} value={value[f.key]} onChange={(v) => set(f.key, v)}
              options={f.options} max={f.max} customLabel={f.custom} placeholder={f.placeholder}
            />
          )
        )}
        <ComboField
          label="Backstory idea" optional testid="backstory" value={value.backstory} onChange={(v) => set("backstory", v)}
          options={BACKSTORY} customLabel="Custom Backstory…" customMax={500} placeholder="Choose a backstory"
        />
      </div>

      {/* live summary */}
      <div className="rounded-3xl border border-[#4A235A]/10 bg-white p-5 sm:p-6" data-testid="builder-summary">
        <h3 className="mb-2 font-display text-lg font-bold text-[#4A235A]">Your character so far</h3>
        <SummaryRow label="Name" value={value.name} fieldId="name" />
        <SummaryRow label={isPet ? "Fantasy form" : "Creature type"} value={value.type} fieldId="type" />
        <SummaryRow label="Image style" value={value.visual_style} fieldId="style" />
        <SummaryRow label="Personality" value={value.personality} fieldId="personality" />
        <SummaryRow label="Role" value={value.role} fieldId="role" />
        <SummaryRow label="Appearance" value={value.appearance} fieldId="appearance" />
        <SummaryRow label="Powers" value={value.powers} fieldId="powers" />
        <SummaryRow label="Background" value={value.background} fieldId="background" />
        <SummaryRow label="Pose" value={value.pose} fieldId="pose" />
        <SummaryRow label="Backstory" value={value.backstory} fieldId="backstory" />
      </div>

      {missing.length > 0 && (
        <p className="text-sm font-semibold text-[#9B5DE5]" data-testid="builder-missing">
          Still needed: {missing.join(", ")}.
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={onBack}
          data-testid="builder-back"
          className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full border border-[#4A235A]/20 px-6 font-bold text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white"
        >
          <ArrowLeft size={18} /> Back
        </button>
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          data-testid="builder-continue"
          className="inline-flex min-h-[56px] flex-1 items-center justify-center gap-2 rounded-full bg-[#4A235A] px-8 text-base font-extrabold text-white transition-transform hover:-translate-y-1 disabled:opacity-50 sm:flex-none"
        >
          {submitting ? <Wand2 size={20} className="animate-pulse" /> : <ArrowRight size={20} />}
          Continue to Review
        </button>
      </div>
    </div>
  );
}
